import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Package,
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  Save,
  ShoppingCart,
  AlertCircle
} from 'lucide-react';
import { usePurchaseOrders } from '../hooks/usePurchaseOrders';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { DecimalKeypad, TextKeypad } from '@/components/ui/keypads';
import type { PurchaseOrder, PurchaseOrderItem } from '@/types/purchase-order.types';

interface ReceivedItem extends PurchaseOrderItem {
  receivedQuantity: number;
  confirmedQuantity: number;
  actualUnitCost: number;
  actualTotalCost: number;
  discrepancy: number;
  notes?: string;
}

export const PurchaseOrderReceivePage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { selectedLocation, user } = useAuth();
  const currentLocationId = selectedLocation?.id || 'loc-1';

  // Check if user is CAJERO (positionId: 3)
  const isCajero = user?.positionId === 3;

  const {
    orders,
    loading,
    error,
    receiveOrder,
    fetchOrder
  } = usePurchaseOrders(currentLocationId);

  const [order, setOrder] = useState<PurchaseOrder | null>(null);
  const [receivedItems, setReceivedItems] = useState<ReceivedItem[]>([]);
  const [generalNotes, setGeneralNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Keypad states for CAJERO
  const [quantityKeypadOpen, setQuantityKeypadOpen] = useState(false);
  const [notesKeypadOpen, setNotesKeypadOpen] = useState(false);
  const [generalNotesKeypadOpen, setGeneralNotesKeypadOpen] = useState(false);
  const [activeItemIndex, setActiveItemIndex] = useState<number | null>(null);

  useEffect(() => {
    if (orderId && orders.length > 0) {
      const foundOrder = orders.find(o => o.id === parseInt(orderId) || o.id === orderId);
      if (foundOrder) {
        setOrder(foundOrder);
        // Initialize received items with original data
        const initialReceivedItems: ReceivedItem[] = foundOrder.items.map(item => ({
          ...item,
          receivedQuantity: item.quantity,
          confirmedQuantity: item.quantity,
          actualUnitCost: item.unitCost, // Mantener para compatibilidad
          actualTotalCost: item.quantity * item.unitCost, // Calcular basado en cantidad confirmada y precio original
          discrepancy: 0,
          notes: ''
        }));
        setReceivedItems(initialReceivedItems);
      }
    }
  }, [orderId, orders]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-PE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLocationName = (locationId: string) => {
    const locations: { [key: string]: string } = {
      'loc-1': 'Sede Principal',
      'loc-2': 'Sucursal Norte',
      'loc-3': 'Sucursal Sur'
    };
    return locations[locationId] || 'Desconocida';
  };

  const updateReceivedItem = (index: number, field: keyof ReceivedItem, value: number | string) => {
    const updatedItems = [...receivedItems];
    const item = updatedItems[index];

    if (field === 'receivedQuantity') {
      item.receivedQuantity = Number(value);
      item.actualTotalCost = item.receivedQuantity * item.unitCost; // Calcular total basado en cantidad recibida
      item.discrepancy = item.receivedQuantity - item.quantity; // Discrepancia basada en cantidad recibida vs solicitada
    } else if (field === 'notes') {
      item.notes = String(value);
    }

    setReceivedItems(updatedItems);
  };

  // Handlers for CAJERO keypads
  const handleQuantityClick = (index: number) => {
    if (isCajero) {
      setActiveItemIndex(index);
      setQuantityKeypadOpen(true);
    }
  };

  const handleNotesClick = (index: number) => {
    if (isCajero) {
      setActiveItemIndex(index);
      setNotesKeypadOpen(true);
    }
  };

  const handleQuantityKeypadSubmit = (value: string) => {
    if (activeItemIndex !== null) {
      updateReceivedItem(activeItemIndex, 'receivedQuantity', parseFloat(value) || 0);
    }
    setQuantityKeypadOpen(false);
    setActiveItemIndex(null);
  };

  const handleNotesKeypadSubmit = (value: string) => {
    if (activeItemIndex !== null) {
      updateReceivedItem(activeItemIndex, 'notes', value);
    }
    setNotesKeypadOpen(false);
    setActiveItemIndex(null);
  };

  const handleGeneralNotesKeypadSubmit = (value: string) => {
    setGeneralNotes(value);
    setGeneralNotesKeypadOpen(false);
  };

  const calculateTotals = () => {
    const subtotal = receivedItems.reduce((sum, item) => sum + item.actualTotalCost, 0);
    const tax = subtotal * 0.18;
    const total = subtotal + tax;
    
    return { subtotal, tax, total };
  };

  const hasDiscrepancies = () => {
    return receivedItems.some(item => item.discrepancy !== 0);
  };

  const handleSubmit = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    if (!order) return;

    setIsSubmitting(true);
    try {
      const success = await receiveOrder(
        order.id,
        receivedItems.map(item => ({
          id: item.id,
          receivedQuantity: item.receivedQuantity,
          notes: item.notes || ''
        })),
        generalNotes || 'Mercadería recibida desde interfaz de recepción'
      );

      if (success) {
        setShowConfirmModal(false);
        // Redirigir a la bandeja de órdenes de compra después de confirmar la recepción
        navigate('/purchase-orders');
      }
    } catch (error) {
      console.error('Error receiving order:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-pulse" />
          <h2 className="text-xl font-semibold text-gray-700">Cargando orden de compra...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Error al cargar orden</h2>
          <p className="text-red-600">{error}</p>
          <Button 
            onClick={() => navigate('/purchase-orders')} 
            className="mt-4"
            variant="outline"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Órdenes
          </Button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Orden no encontrada</h2>
          <p className="text-gray-600">La orden de compra solicitada no existe o no está disponible para recepción.</p>
          <Button 
            onClick={() => navigate('/purchase-orders')} 
            className="mt-4"
            variant="outline"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Órdenes
          </Button>
        </div>
      </div>
    );
  }

  if (order.status !== 'approved') {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Orden no disponible para recepción</h2>
          <p className="text-gray-600">Solo las órdenes aprobadas pueden ser recibidas.</p>
          <Button 
            onClick={() => navigate(`/purchase-orders/${order.id}`)} 
            className="mt-4"
            variant="outline"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Ver Detalles de Orden
          </Button>
        </div>
      </div>
    );
  }

  const { subtotal, tax, total } = calculateTotals();

  return (
    <div className="relative h-full">
      <div className="p-3 md:p-6 space-y-6 h-full overflow-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/purchase-orders/${order.id}`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Recibir Mercadería</h1>
            </div>
            <p className="text-gray-600 text-sm md:text-base">
              {order.orderNumber} - {order.supplierName}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-green-600 hover:bg-green-700"
          >
            {isSubmitting ? (
              <>
                <Package className="w-4 h-4 mr-2 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirmar Recepción
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Top Section - Information, Comparison, and Summary - Hidden for CAJERO */}
      {!isCajero && (
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
          {/* Order Info - 3 columns (más espacio) */}
          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-macartur-primary" />
                Información de la Orden
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Fecha de Orden:</p>
                  <p className="font-semibold">{formatDate(order.createdAt)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Sucursal:</p>
                  <p className="font-semibold">{getLocationName(order.locationId)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Fecha de Aprobación:</p>
                  <p className="font-semibold">{order.approvedAt ? formatDate(order.approvedAt) : 'No disponible'}</p>
                </div>
                <div>
                  <p className="text-gray-600">Usuario Aprobación:</p>
                  <p className="font-semibold">{order.approvedBy || 'No disponible'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Comparison - 1 column (angosta) */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle>Comparación</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-600">Total Original:</p>
                  <p className="font-semibold">{formatCurrency(order.total)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Recibido:</p>
                  <p className="font-semibold">{formatCurrency(total)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Diferencia:</p>
                  <p className={`font-semibold ${
                    total - order.total > 0 ? 'text-red-600' :
                    total - order.total < 0 ? 'text-green-600' :
                    'text-gray-900'
                  }`}>
                    {total - order.total > 0 ? '+' : ''}{formatCurrency(total - order.total)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary - 2 columns (más angosto) */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Resumen de Recepción</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Items:</span>
                  <span>{receivedItems.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Con discrepancias:</span>
                  <span className="text-orange-600 font-medium">
                    {receivedItems.filter(item => item.discrepancy !== 0).length}
                  </span>
                </div>
                <div className="border-t pt-2 mt-2" />
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-semibold">{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">IGV (18%):</span>
                  <span className="font-semibold">{formatCurrency(tax)}</span>
                </div>
                <div className="border-t pt-2 mt-2" />
                <div className="flex justify-between text-lg">
                  <span className="font-semibold">Total Recibido:</span>
                  <span className="font-bold text-macartur-primary">{formatCurrency(total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Items Section - Full Width */}
      <div className="space-y-6">
        {/* Items to Receive */}
        <Card>
          <CardHeader>
            <CardTitle>Mercadería a Recibir</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Insumo
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Solicitado
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cantidad Recibida
                      </th>
                      {!isCajero && (
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Precio Unitario
                        </th>
                      )}
                      {!isCajero && (
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Real
                        </th>
                      )}
                      {!isCajero && (
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Discrepancia
                        </th>
                      )}
                      {!isCajero && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Notas
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {receivedItems.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {item.ingredientName}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <div className="text-sm text-gray-900">
                            {item.quantity} {item.unit}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <Input
                            type="number"
                            value={item.receivedQuantity}
                            onChange={(e) => !isCajero && updateReceivedItem(index, 'receivedQuantity', e.target.value)}
                            onClick={() => handleQuantityClick(index)}
                            min={0}
                            step={0.01}
                            className={`w-24 text-center ${isCajero ? 'cursor-pointer' : ''}`}
                            readOnly={isCajero}
                          />
                        </td>
                        {!isCajero && (
                          <td className="px-4 py-4 text-center">
                            <div className="text-sm text-gray-900">
                              {formatCurrency(item.unitCost)}
                            </div>
                          </td>
                        )}
                        {!isCajero && (
                          <td className="px-4 py-4 text-center">
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(item.actualTotalCost)}
                            </div>
                          </td>
                        )}
                        {!isCajero && (
                          <td className="px-4 py-4 text-center">
                            {item.discrepancy !== 0 ? (
                              <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                                {item.discrepancy > 0 ? '+' : ''}{item.discrepancy}
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="bg-green-100 text-green-800">
                                OK
                              </Badge>
                            )}
                          </td>
                        )}
                        {!isCajero && (
                          <td className="px-4 py-4">
                            <Input
                              value={item.notes || ''}
                              onChange={(e) => updateReceivedItem(index, 'notes', e.target.value)}
                              placeholder="Notas..."
                              className="text-sm min-w-[180px]"
                            />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* General Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Notas Generales de Recepción</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={generalNotes}
                onChange={(e) => !isCajero && setGeneralNotes(e.target.value)}
                onClick={() => isCajero && setGeneralNotesKeypadOpen(true)}
                placeholder="Observaciones generales sobre la recepción de mercadería..."
                rows={4}
                className={isCajero ? 'cursor-pointer' : ''}
                readOnly={isCajero}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirmar Recepción de Mercadería
            </h3>
            <p className="text-gray-600 mb-6">
              ¿Está seguro que desea confirmar la recepción de esta orden de compra?
              {hasDiscrepancies() && (
                <span className="block mt-2 text-amber-600 font-medium">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Se han detectado discrepancias en cantidades o precios.
                </span>
              )}
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowConfirmModal(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmSubmit}
                disabled={isSubmitting}
                className="bg-green-600 hover:bg-green-700"
              >
                {isSubmitting ? (
                  <>
                    <Package className="w-4 h-4 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Confirmar Recepción
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Keypads for CAJERO */}
      {isCajero && (
        <>
          {/* Quantity Keypad */}
          <DecimalKeypad
            isOpen={quantityKeypadOpen}
            onClose={() => {
              setQuantityKeypadOpen(false);
              setActiveItemIndex(null);
            }}
            onSubmit={handleQuantityKeypadSubmit}
            title={`Cantidad recibida: ${activeItemIndex !== null && receivedItems[activeItemIndex] ? receivedItems[activeItemIndex].ingredientName : ''}`}
            initialValue={activeItemIndex !== null && receivedItems[activeItemIndex] ? Number(receivedItems[activeItemIndex].receivedQuantity).toFixed(2) : '0.00'}
          />

          {/* Item Notes Keypad */}
          <TextKeypad
            isOpen={notesKeypadOpen}
            onClose={() => {
              setNotesKeypadOpen(false);
              setActiveItemIndex(null);
            }}
            onSubmit={handleNotesKeypadSubmit}
            title={`Notas: ${activeItemIndex !== null ? receivedItems[activeItemIndex]?.ingredientName : ''}`}
            initialValue={activeItemIndex !== null ? (receivedItems[activeItemIndex]?.notes || '') : ''}
            placeholder="INGRESE NOTAS..."
          />

          {/* General Notes Keypad */}
          <TextKeypad
            isOpen={generalNotesKeypadOpen}
            onClose={() => setGeneralNotesKeypadOpen(false)}
            onSubmit={handleGeneralNotesKeypadSubmit}
            title="Notas Generales de Recepción"
            initialValue={generalNotes}
            placeholder="OBSERVACIONES GENERALES..."
          />
        </>
      )}
    </div>
  );
};